/** GP review flags — aligned with app/utils/menuBuilderHomeCard.js */

import { formatGbp } from 'src/utils/formatGbp'
import { parseGpPercent } from 'src/utils/recipeGp'
import { isRecipeArchived } from 'src/utils/recipeMonitoring'

export const recipeNeedsGpReview = recipe =>
  !isRecipeArchived(recipe) &&
  Boolean(
    recipe?.needsReview ||
      recipe?.gpNeedsReview ||
      recipe?.hasGpChanges ||
      recipe?.gpChanged,
  )

export const countRecipesNeedingGpReview = (recipes = []) =>
  recipes.filter(recipeNeedsGpReview).length

/**
 * A shared recipe whose ingredients aren't priced at this venue comes back as
 * cost 0 and GP 0. That is "no prices yet", not a real 0% GP.
 */
export const recipeHasNoPricesYet = recipe => {
  const cost = Number(recipe?.costPerRecipe)
  const noCost =
    recipe?.costPerRecipe == null ||
    recipe?.costPerRecipe === '' ||
    (Number.isFinite(cost) && cost <= 0)
  const gp = parseGpPercent(recipe?.grossProfit)
  return noCost && (gp === null || gp === 0)
}

export const GP_REVIEW_NO_PRICES_YET = 'No prices yet'

export const formatNoPricesYetCount = count =>
  count > 0 ? `${count} with no prices yet` : null

export const formatGpPercentForReview = grossProfit => {
  const gp = parseGpPercent(grossProfit)
  if (gp === null) {
    return null
  }

  return `${Number.isInteger(gp) ? gp : gp.toFixed(1)}%`
}

export const formatGpReviewDelta = (previousGp, currentGp) => {
  const previous = formatGpPercentForReview(previousGp)
  const current = formatGpPercentForReview(currentGp)

  if (!previous || !current || previous === current) {
    return null
  }

  return `${previous} → ${current}`
}

export const formatCostReviewDelta = (previousCost, currentCost) => {
  const previous = Number(previousCost)
  const current = Number(currentCost)

  if (!Number.isFinite(previous) || !Number.isFinite(current)) {
    return null
  }

  if (Math.abs(previous - current) < 0.005) {
    return null
  }

  return `${formatGbp(previous)} → ${formatGbp(current)}`
}

export const hasGpReviewDelta = recipe =>
  Boolean(
    formatGpReviewDelta(recipe?.previousGrossProfit, recipe?.grossProfit) ||
      formatCostReviewDelta(
        recipe?.previousCostPerRecipe,
        recipe?.costPerRecipe,
      ),
  )

export const formatGpReviewBannerLine = (
  recipe,
  { prefix = 'GP changed' } = {},
) => {
  const delta = formatGpReviewDelta(
    recipe?.previousGrossProfit,
    recipe?.grossProfit,
  )

  if (!delta) {
    return null
  }

  return `${prefix}: ${delta}`
}

export const buildGpReviewBannerText = (recipe, copy = {}) => {
  if (recipeHasNoPricesYet(recipe)) {
    return copy?.noPricesYetBanner || GP_REVIEW_NO_PRICES_YET
  }

  const gpDelta = formatGpReviewDelta(
    recipe?.previousGrossProfit,
    recipe?.grossProfit,
  )

  if (gpDelta) {
    if (copy?.gpChangedBannerTemplate) {
      return copy.gpChangedBannerTemplate.replace('{delta}', gpDelta)
    }

    return formatGpReviewBannerLine(recipe, {
      prefix: copy?.gpChangedBannerPrefix || 'GP changed',
    })
  }

  const costDelta = formatCostReviewDelta(
    recipe?.previousCostPerRecipe,
    recipe?.costPerRecipe,
  )

  if (!costDelta) {
    return null
  }

  if (copy?.costChangedBannerTemplate) {
    return copy.costChangedBannerTemplate.replace('{delta}', costDelta)
  }

  return `${copy?.costChangedBannerPrefix || 'Cost changed'}: ${costDelta}`
}

export const recipeShowsGpReviewBanner = recipe =>
  recipeNeedsGpReview(recipe) && hasGpReviewDelta(recipe)
